import { useState, useMemo, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { toast } from "@/hooks/use-toast";
import { format } from "date-fns";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import { useAdminSalonWaitlist } from "@/hooks/useAdminSalonWaitlist";
import { displayAdminEventLabel } from "@/lib/datetime";

const PAGE_SIZE = 20;

const SalonWaitlist = () => {
  const queryClient = useQueryClient();
  const { data: waitlistData, isLoading } = useAdminSalonWaitlist();
  const [activeTab, setActiveTab] = useState("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [activeTab]);

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const updateData: any = { status };
      if (status === "invited") {
        updateData.invited_at = new Date().toISOString();
      }
      const { error } = await supabase
        .from("salon_waitlist")
        .update(updateData)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-salon-waitlist"] });
      toast({
        title: "Status updated",
        description: "Waitlist status has been updated successfully.",
      });
    },
    onError: (error) => {
      console.error("Error updating status:", error);
      toast({
        title: "Error",
        description: "Failed to update status. Please try again.",
        variant: "destructive",
      });
    },
  });

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      pending: "secondary",
      invited: "outline",
      booked: "default",
      declined: "destructive",
    };
    return (
      <Badge variant={variants[status] || "outline"}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const filterByStatus = (status?: string) => {
    if (!waitlistData) return [];
    if (!status) return waitlistData;
    return waitlistData.filter((entry) => entry.status === status);
  };

  const currentData = useMemo(
    () => (activeTab === "all" ? filterByStatus() : filterByStatus(activeTab)),
    [activeTab, waitlistData]
  );

  const totalPages = Math.max(1, Math.ceil(currentData.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginatedData = currentData.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!waitlistData || waitlistData.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No Secret Salon waitlist requests yet.</p>
      </div>
    );
  }

  const renderTable = (data: any[]) => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>User</TableHead>
          <TableHead>Event</TableHead>
          <TableHead>Requested</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((entry) => (
          <TableRow key={entry.id}>
            <TableCell>
              <div>
                <div className="font-medium">
                  {entry.profiles?.first_name} {entry.profiles?.last_name}
                </div>
                <div className="text-sm text-muted-foreground">
                  {entry.profiles?.email}
                </div>
              </div>
            </TableCell>
            <TableCell>
              <div>
                <div className="font-medium">
                  {entry.events?.title && entry.events?.start_time
                    ? displayAdminEventLabel(entry.events.title, entry.events.start_time, entry.events.timezone || undefined)
                    : entry.events?.title}
                </div>
              </div>
            </TableCell>
            <TableCell>
              {format(new Date(entry.created_at), "MMM d, yyyy")}
            </TableCell>
            <TableCell>{getStatusBadge(entry.status)}</TableCell>
            <TableCell>
              <div className="flex gap-2">
                {entry.status === "pending" && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateStatusMutation.mutate({ id: entry.id, status: "invited" })}
                      disabled={updateStatusMutation.isPending}
                    >
                      Mark Invited
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => updateStatusMutation.mutate({ id: entry.id, status: "declined" })}
                      disabled={updateStatusMutation.isPending}
                    >
                      Decline
                    </Button>
                  </>
                )}
                {entry.status === "invited" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateStatusMutation.mutate({ id: entry.id, status: "pending" })}
                    disabled={updateStatusMutation.isPending}
                  >
                    Move to Pending
                  </Button>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
    return (
      <div className="flex items-center justify-between pt-4">
        <p className="text-sm text-muted-foreground">
          Showing {(safePage - 1) * PAGE_SIZE + 1}-
          {Math.min(safePage * PAGE_SIZE, currentData.length)} of {currentData.length}
        </p>
        <Pagination className="mx-0 w-auto justify-end">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={(e) => {
                  e.preventDefault();
                  if (safePage > 1) setPage(safePage - 1);
                }}
                className={safePage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>
            {pages.map((p) => (
              <PaginationItem key={p}>
                <PaginationLink
                  isActive={p === safePage}
                  onClick={(e) => {
                    e.preventDefault();
                    setPage(p);
                  }}
                  className="cursor-pointer"
                >
                  {p}
                </PaginationLink>
              </PaginationItem>
            ))}
            <PaginationItem>
              <PaginationNext
                onClick={(e) => {
                  e.preventDefault();
                  if (safePage < totalPages) setPage(safePage + 1);
                }}
                className={
                  safePage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"
                }
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-semibold">Secret Salon Waitlist</h2>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All ({waitlistData.length})</TabsTrigger>
          <TabsTrigger value="pending">
            Pending ({filterByStatus("pending").length})
          </TabsTrigger>
          <TabsTrigger value="invited">
            Invited ({filterByStatus("invited").length})
          </TabsTrigger>
          <TabsTrigger value="booked">
            Booked ({filterByStatus("booked").length})
          </TabsTrigger>
          <TabsTrigger value="declined">
            Declined ({filterByStatus("declined").length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          {renderTable(paginatedData)}
          {renderPagination()}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SalonWaitlist;
